import { has, upperFirst } from 'lodash';
import styled from 'styled-components/macro';

import { AppConstants } from '../../constants/AppConstants';
import { FieldItem } from './FieldItem';
import { useQuery } from '../../hooks/queryParams';
import InfoPopover from '../shared/Fields/InfoPopover';
import { Change } from '../../assets/components/svgs';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import { deleteSystemSettingFromOrg } from '../../http/configuration-services';
import BiButton from '../primitives/buttons/BiButton.primitive';
import { useState } from 'react';
import { HTTP_STATUS } from '../../http/constants/http.status';
import { setToastData } from '../../app/toastReducer';
import { getFailureMessage } from '../../util/ErrorUtil';
import Modal from '../shared/ModalPortal/Modal';
import { ExclamationIcon } from '@heroicons/react/outline';
import { setMobileConfigFlags } from '../../app/mobileConfigurationReducer';

const BorderContainer = styled.div<{ showborder: boolean }>`
  border-bottom: ${({ showborder }) => (showborder ? '1px solid #d9d9d9' : 'unset')};
`;

interface FieldLayoutProps {
  fieldGroup: any;
  fieldItem: any;
  fieldValue: any;
  fieldErrors: Array<string>;
  onValueChange: Function;
  lastItem: boolean;
}

export const FieldLayout: React.FC<FieldLayoutProps> = ({
  fieldGroup,
  fieldItem,
  fieldValue,
  fieldErrors,
  onValueChange,
  lastItem = false
}: FieldLayoutProps) => {
  const { attributes } = fieldGroup;
  const { layout: grpLayout } = attributes;
  const { value: grpValue } = grpLayout;

  const isEdit = useAppSelector((state) => state.mobileConfiguration.flags.isEdit);
  const selectedOrg: any = useAppSelector((state) => state.mobileConfiguration.selectedConfigOrg);

  const { layout = {}, metadata, value: globalValue, valueConfig } = fieldItem;
  const { value: configValue } = valueConfig;
  const { attributes: layoutParams = {} } = layout;
  const {
    display = AppConstants.DISPLAY_INLINE,
    columnSpan = AppConstants.PAGE_LAYOUT_MONO,
    divider = false
  } = layoutParams;
  const { attributes: metaParams } = metadata;
  const { description, displayName } = metaParams;

  let direction: string = display;
  const { t } = useTranslation();

  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const query = useQuery();
  if (query.get('display')) {
    direction = query.get('display').toUpperCase();
  }

  const checkFlag = () => {
    const { valueConfig } = fieldItem;
    if (selectedOrg.length) {
      const [selection] = selectedOrg;
      if (selection?.id) {
        return (
          has(valueConfig, 'systemSettingId') &&
          valueConfig.systemSettingId !== valueConfig.id &&
          selection?.id === valueConfig.organizationId
        );
      }
    }
    return false;
  };

  const deleteOverride = async () => {
    try {
      dispatch(setMobileConfigFlags({ loading: true }));
      const { organizationId = '', systemSettingId = '', id = '' } = valueConfig;
      let response = await deleteSystemSettingFromOrg(organizationId, id);

      const { data, status } = response;
      if (HTTP_STATUS.isSuccess(status)) {
        dispatch(
          setToastData({
            toastMessage: t('T_OVERRIDE_DELETION_SUCCESS'),
            isToastActive: true,
            toastType: 'success'
          })
        );
        dispatch(setMobileConfigFlags({ recallConfig: true }));
      } else {
        dispatch(setMobileConfigFlags({ loading: false }));
        dispatch(
          setToastData({
            toastMessage: getFailureMessage(response),
            isToastActive: true,
            toastType: 'error'
          })
        );
      }
    } catch (e) {
      dispatch(setMobileConfigFlags({ loading: false }));
      dispatch(
        setToastData({
          toastMessage: t('T_ERROR_SOMETHING_WRONG'),
          isToastActive: true,
          toastType: 'error'
        })
      );
    }
  };

  const popoverContent = () => {
    if (!isEdit) {
      return (
        <div className="mx-2 my-1.5 w-48 text-sm font-normal">
          <div>{t('T_OVERRIDE_ORGANIZATION_HELPER')}</div>
          <div
            className="mt-2 cursor-pointer text-primary"
            onClick={() => {
              setShowDeletePopup(true);
            }}
          >
            {t('T_OVERRIDE_ORGANIZATION_DELETE')}
          </div>
        </div>
      );
    } else {
      {
        return (
          <div className="mx-2 my-1.5 w-48 text-sm font-normal">
            <div>{t('T_OVERRIDE_ORGANIZATION_INFO')}</div>
          </div>
        );
      }
    }
  };

  const deleteConfirmation = () => {
    return (
      <div>
        <div>{t('T_OVERRIDE_DELETION_HELPER')}</div>
        <div className="mt-8 flex">
          <BiButton
            className={'ml-auto mr-2 text-primary'}
            type="button"
            onClick={() => {
              setShowDeletePopup(false);
            }}
          >
            {t('T_CANCEL')}
          </BiButton>
          <BiButton className={'bg-primary text-white'} type="button" onClick={deleteOverride}>
            {t('T_DELETE_CONFIRM')}
          </BiButton>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col`}
      style={{ width: `${((columnSpan / grpValue) * 100 - 1).toFixed(2)}%` }}
    >
      {showDeletePopup ? (
        <Modal
          showCloseIcon={true}
          width="42rem"
          headerText={t('T_CONFIRM_DELETION')}
          toggleVisiblity={(value: boolean) => {
            setShowDeletePopup(value);
          }}
          isVisible={showDeletePopup}
          body={deleteConfirmation()}
          headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
        />
      ) : (
        ''
      )}
      <BorderContainer
        className={`${'mt-3 flex w-full pb-3'} ${
          direction === AppConstants.DISPLAY_INLINE ? 'flex-row justify-between ' : 'flex-col'
        } `}
        showborder={divider && !lastItem}
      >
        <div className={'flex-1'}>
          <span className={`$text-base flex flex-row font-medium`}>
            {upperFirst(displayName)}
            {checkFlag() ? (
              <InfoPopover
                className="ml-2"
                customIcon={
                  <div className="mb-1 flex h-6 w-6 items-center justify-center rounded-full bg-[#EAF5FA]">
                    {Change(!isEdit ? '#1745D1' : '#595959')}
                  </div>
                }
                content={popoverContent()}
              ></InfoPopover>
            ) : (
              ''
            )}
          </span>
          {description && (
            <div className={`mb-1 text-sm font-normal`} style={{ color: '#262626' }}>
              {description}
            </div>
          )}
        </div>
        <FieldItem
          fieldItem={fieldItem}
          fieldValue={fieldValue || globalValue || configValue}
          fieldErrors={fieldErrors}
          onValueChange={onValueChange}
          direction={direction}
        />
      </BorderContainer>
    </div>
  );
};
